import { IsBoolean, IsIn, IsInt, IsOptional, IsString, Matches, MaxLength } from 'class-validator';

/**
 * The two Stage 1 lists an administrator controls: intake method (§A) and
 * subject-property category (§D).
 *
 * Both were fixed unions in this file until administrators needed to extend
 * them. Neither is a label list, so every row carries its behaviour explicitly:
 * an intake method selects which conditional intake section opens, and a
 * property type decides whether Stage 2 shows the Entry & Access Module. A rule
 * that guessed from the name would be inventing statutory routing.
 *
 * Keys are stable and never rewritten — files and complaints store them.
 */

/** Key format: lowercase snake_case, so it reads the same as the seeded values. */
export const TAXONOMY_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

export type ComplaintSourceKind = 'citizen' | 'internal';
export const COMPLAINT_SOURCE_KINDS: ComplaintSourceKind[] = ['citizen', 'internal'];

/** One administrable intake method, as returned by the API. */
export interface IntakeMethodOption {
  id: string;
  key: string;
  labelEn: string;
  labelFr: string;
  /** Opens §B — complainant / reporter information. */
  opensComplainantSection: boolean;
  /** Opens §C — internal source detail. Both may be open at once. */
  opensInternalSourceSection: boolean;
  isCouncilReferral: boolean;
  source: ComplaintSourceKind;
  isActive: boolean;
  sortOrder: number;
  /** How many complaints already reference this key. Blocks deletion when > 0. */
  usageCount?: number;
}

/** One administrable subject-property category, as returned by the API. */
export interface PropertyTypeOption {
  id: string;
  key: string;
  labelEn: string;
  labelFr: string;
  /** Whether entry needs a private-property entry authority (Stage 2 module). */
  requiresEntryAuthority: boolean;
  isActive: boolean;
  sortOrder: number;
  /** How many files already reference this key. Blocks deletion when > 0. */
  usageCount?: number;
}

/** Pick the label for the active locale, falling back to English. */
export function taxonomyLabel(
  option: { labelEn: string; labelFr: string },
  locale: string | null | undefined,
): string {
  return locale?.startsWith('fr') ? option.labelFr || option.labelEn : option.labelEn;
}

/**
 * Whether §B should be collected for a method.
 *
 * A key with no matching row opens it. That is the same fail-open bias the
 * previous hardcoded predicate took for `other` and for a null method: asking
 * for a contact the officer may leave blank costs nothing, while hiding the
 * section silently loses the only record of who reported the matter.
 */
export function opensComplainantSection(
  method: string | null,
  options: IntakeMethodOption[],
): boolean {
  if (method === null) return true;
  return options.find((o) => o.key === method)?.opensComplainantSection ?? true;
}

/** Whether §C should be collected. Same fail-open bias as §B. */
export function opensInternalSourceSection(
  method: string | null,
  options: IntakeMethodOption[],
): boolean {
  if (method === null) return true;
  return options.find((o) => o.key === method)?.opensInternalSourceSection ?? true;
}

/**
 * Whether Stage 2 renders the Entry & Access Module for a property type.
 *
 * Unknown and null both return true — a type the system cannot classify must
 * show the officer the entry pathway rather than silently removing it.
 */
export function requiresEntryAuthority(
  propertyType: string | null,
  options: PropertyTypeOption[],
): boolean {
  if (propertyType === null) return true;
  return options.find((o) => o.key === propertyType)?.requiresEntryAuthority ?? true;
}

// ---------------------------------------------------------------------------
// Admin write DTOs
// ---------------------------------------------------------------------------

export class CreateIntakeMethodDto {
  @IsString()
  @MaxLength(32)
  @Matches(TAXONOMY_KEY_PATTERN, {
    message: 'key must be lowercase snake_case, e.g. drone_report',
  })
  key!: string;

  @IsString()
  @MaxLength(120)
  labelEn!: string;

  @IsString()
  @MaxLength(120)
  labelFr!: string;

  @IsOptional()
  @IsBoolean()
  opensComplainantSection?: boolean;

  @IsOptional()
  @IsBoolean()
  opensInternalSourceSection?: boolean;

  @IsOptional()
  @IsBoolean()
  isCouncilReferral?: boolean;

  @IsOptional()
  @IsIn(COMPLAINT_SOURCE_KINDS)
  source?: ComplaintSourceKind;

  @IsOptional()
  @IsBoolean()
  isActive?: boolean;

  @IsOptional()
  @IsInt()
  sortOrder?: number;
}

/** The key is immutable — files reference it, so a rename would orphan them. */
export class UpdateIntakeMethodDto {
  @IsOptional()
  @IsString()
  @MaxLength(120)
  labelEn?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  labelFr?: string;

  @IsOptional()
  @IsBoolean()
  opensComplainantSection?: boolean;

  @IsOptional()
  @IsBoolean()
  opensInternalSourceSection?: boolean;

  @IsOptional()
  @IsBoolean()
  isCouncilReferral?: boolean;

  @IsOptional()
  @IsIn(COMPLAINT_SOURCE_KINDS)
  source?: ComplaintSourceKind;

  @IsOptional()
  @IsBoolean()
  isActive?: boolean;

  @IsOptional()
  @IsInt()
  sortOrder?: number;
}

export class CreatePropertyTypeDto {
  @IsString()
  @MaxLength(32)
  @Matches(TAXONOMY_KEY_PATTERN, {
    message: 'key must be lowercase snake_case, e.g. boardwalk',
  })
  key!: string;

  @IsString()
  @MaxLength(120)
  labelEn!: string;

  @IsString()
  @MaxLength(120)
  labelFr!: string;

  @IsOptional()
  @IsBoolean()
  requiresEntryAuthority?: boolean;

  @IsOptional()
  @IsBoolean()
  isActive?: boolean;

  @IsOptional()
  @IsInt()
  sortOrder?: number;
}

export class UpdatePropertyTypeDto {
  @IsOptional()
  @IsString()
  @MaxLength(120)
  labelEn?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  labelFr?: string;

  @IsOptional()
  @IsBoolean()
  requiresEntryAuthority?: boolean;

  @IsOptional()
  @IsBoolean()
  isActive?: boolean;

  @IsOptional()
  @IsInt()
  sortOrder?: number;
}
